<template>
  <section class="quick-font-settings" aria-labelledby="quick-font-title">
    <div class="quick-font-heading">
      <span class="quick-font-icon"><i class="fas fa-text-height"></i></span>
      <div>
        <strong id="quick-font-title">Tamanho do texto na etiqueta</strong>
        <small>
          Esta alteração será salva no modelo e usada na prévia, impressão teste
          e impressão final.
        </small>
      </div>
    </div>

    <div v-if="fontElements.length" class="quick-font-list">
      <div
        v-for="element in fontElements"
        :key="element.id"
        class="quick-font-row"
        :data-element-id="element.id"
      >
        <div class="quick-font-identity">
          <strong :title="element.nome || typeLabel(element)">
            {{ element.nome || typeLabel(element) }}
          </strong>
          <small>{{ typeLabel(element) }}</small>
        </div>

        <div
          class="quick-font-control"
          role="group"
          :aria-label="`Tamanho da fonte de ${
            element.nome || typeLabel(element)
          }`"
        >
          <button
            type="button"
            :disabled="fontSize(element) <= minSize"
            :aria-label="`Diminuir fonte de ${
              element.nome || typeLabel(element)
            }`"
            @click="adjust(element, -step)"
          >
            <i class="fas fa-minus"></i>
          </button>
          <label>
            <span class="sr-only"
              >Tamanho da fonte de
              {{ element.nome || typeLabel(element) }}</span
            >
            <input
              type="number"
              :min="minSize"
              :max="maxSize"
              :step="step"
              :value="fontSize(element)"
              @change="applyInput(element, $event)"
              @keyup.enter="$event.target.blur()"
            />
            <span>pt</span>
          </label>
          <button
            type="button"
            :disabled="fontSize(element) >= maxSize"
            :aria-label="`Aumentar fonte de ${
              element.nome || typeLabel(element)
            }`"
            @click="adjust(element, step)"
          >
            <i class="fas fa-plus"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-else class="quick-font-empty">
      Adicione um texto ou campo à etiqueta para ajustar sua fonte aqui.
    </div>
  </section>
</template>

<script>
export default {
  name: "QuickFontSettings",
  props: {
    elements: {
      type: Array,
      default: () => [],
    },
    printOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      minSize: 4,
      maxSize: 72,
      step: 0.5,
    };
  },
  computed: {
    fontElements() {
      return this.elements.filter((element) => {
        if (["text", "field"].includes(element.tipo)) return true;
        return (
          element.tipo === "barcode" &&
          (element.codigo_barras?.exibir_texto !== false ||
            (this.printOptions.considerar_codigo_interno &&
              this.printOptions.codigo_interno_somente_numeracao))
        );
      });
    },
  },
  methods: {
    fontSize(element) {
      const value = Number(element?.estilo?.tamanho_pt);
      return Number.isFinite(value)
        ? Math.min(this.maxSize, Math.max(this.minSize, value))
        : 8;
    },
    typeLabel(element) {
      if (
        element.tipo === "barcode" &&
        this.printOptions.considerar_codigo_interno &&
        this.printOptions.codigo_interno_somente_numeracao
      ) {
        return "Código interno em número";
      }
      return {
        text: "Texto livre",
        field: "Campo da etiqueta",
        barcode: "Número do código de barras",
      }[element.tipo];
    },
    normalize(value) {
      const numeric = Number(String(value).replace(",", "."));
      if (!Number.isFinite(numeric)) return null;
      const clamped = Math.min(this.maxSize, Math.max(this.minSize, numeric));
      return Math.round(clamped / this.step) * this.step;
    },
    emitSize(element, value) {
      const size = this.normalize(value);
      if (size === null || size === this.fontSize(element)) return;
      this.$emit("change", { id: element.id, size });
    },
    adjust(element, amount) {
      this.emitSize(element, this.fontSize(element) + amount);
    },
    applyInput(element, event) {
      const size = this.normalize(event.target.value);
      if (size === null) {
        event.target.value = this.fontSize(element);
        return;
      }
      event.target.value = size;
      this.emitSize(element, size);
    },
  },
};
</script>

<style lang="scss" scoped>
.quick-font-settings {
  margin: 15px 0;
  padding: 12px;
  border: 1px solid #ded8fb;
  border-radius: 9px;
  background: #faf9ff;
}

.quick-font-heading {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  > div {
    min-width: 0;
  }

  strong,
  small {
    display: block;
  }

  strong {
    color: #3f4254;
    font-size: 10px;
  }

  small {
    margin-top: 3px;
    color: #7e8299;
    font-size: 8px;
    line-height: 1.45;
  }
}

.quick-font-icon {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 7px;
  background: #e9e4ff;
  color: #8950fc;
  display: grid;
  place-items: center;
  font-size: 11px;
}

.quick-font-list {
  margin-top: 11px;
  border-top: 1px solid #ebe7fb;
}

.quick-font-row {
  min-height: 49px;
  padding: 7px 0;
  border-bottom: 1px solid #ebe7fb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
}

.quick-font-identity {
  min-width: 0;
  flex: 1;

  strong,
  small {
    display: block;
  }

  strong {
    overflow: hidden;
    color: #3f4254;
    font-size: 9px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    margin-top: 2px;
    color: #a1a5b7;
    font-size: 7px;
  }
}

.quick-font-control {
  flex: 0 0 auto;
  display: flex;
  align-items: stretch;

  button {
    width: 29px;
    min-width: 29px;
    padding: 0;
    border: 1px solid #d8d3ec;
    background: #fff;
    color: #8950fc;
    font-size: 8px;

    &:first-child {
      border-radius: 6px 0 0 6px;
    }

    &:last-child {
      border-radius: 0 6px 6px 0;
    }

    &:hover:not(:disabled) {
      z-index: 1;
      border-color: #8950fc;
      background: #f0ebff;
    }

    &:disabled {
      color: #c9c7d2;
      cursor: not-allowed;
    }
  }

  label {
    width: 62px;
    min-height: 31px;
    margin: 0 -1px;
    border: 1px solid #d8d3ec;
    background: #fff;
    display: flex;
    align-items: center;

    &:focus-within {
      z-index: 2;
      border-color: #8950fc;
      box-shadow: 0 0 0 2px rgba(137, 80, 252, 0.12);
    }

    input {
      width: 39px;
      padding: 0 2px 0 5px;
      border: 0;
      outline: 0;
      color: #3f4254;
      font-size: 10px;
      font-weight: 700;
      text-align: right;
      appearance: textfield;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        margin: 0;
        appearance: none;
      }
    }

    span:last-child {
      color: #a1a5b7;
      font-size: 8px;
    }
  }
}

.quick-font-empty {
  margin-top: 10px;
  padding: 9px;
  border-radius: 6px;
  background: #f3f1fb;
  color: #7e8299;
  font-size: 8px;
  line-height: 1.45;
  text-align: center;
}
</style>
